'use strict';
/*
    Creates a dropdown menu of options for an input field.
    Header items must contain a truthy `header` property (will not be selectable).

    Configurable via attributes:
        Input field as `inputField`
        Data source as `data`.
        Options format as `headerFormat`, `optionFormat` (defaults to `Title`)
        `select` function receives arguments `(item, e)`.

    Other options defined in `bindToController`.
*/
namespace Autocompleter {

    class FieldOptions {

        private inputField: JQuery;
        private data: any[];
        private select: Function;
        private headerFormat: string;
        private optionFormat: string;
        private noHide: boolean;
        private classes: string;
        private restricted: string;

        uiState = {
                fieldFocused: false,
                menuShowing: false,
                menuPopulated: false,
                selectedIndex: -1,
                selectableOptions: {},
                selectableOpsCount: 0,
                denyHide: false,
                denyScroll: false,
                denyFocus: false,
                denyBlur: false
            };

        createMenu: (inputEl: JQuery) => void;

        constructor(
            private $scope: any,
            private $element: JQuery,
            private $parse: ng.IParseService,
            private $timeout: ng.ITimeoutService,
            private $q: ng.IQService
        ) {
            let self = this;

            // `no-hide` attribute
            if (this.noHide){
                this.uiState.denyHide = true;
                this.uiState.menuShowing = true;
            }
            else {
                $element.hide();
            }

            this.createMenu = (inputEl: JQuery) => {

                let uiState = this.uiState,
                    selectHandler: Function = this.select,
                    formatHeader = $parse(this.headerFormat || 'Title'),
                    formatLabel = $parse(this.optionFormat || 'Title'),
                    ul: JQuery = $element,
                    eventNS: string = '.fieldOptions',
                    selOptionClass: string = 'selected';

                let uiHandlers = {
                        9: selectCurrOption, // Tab
                        13: selectCurrOption, // Return
                        16: angular.noop, // Shift
                        27: blurField, // Escape
                        38: () => traverseOptions(-1), // Up arrow
                        40: () => traverseOptions(1) // Down arrow
                    };

                // Debounced menu refresh (thenable)
                let debouncedPopulateMenu = _.debounce(resolve => {
                        populateMenu(this.data);
                        resolve();
                    }, 100);

                init();


                function init() {
                    // Watch `data` attribute
                    $scope.$watch('fo.data', v => {
                        if (v) {
                            if (!uiState.denyFocus) {
                                refreshMenu().then(showMenu);
                            }
                        }
                        else {
                            hideMenu();
                            clearMenu();
                        }
                    });

                    // DOM listeners on input field
                    inputEl
                        .on('focus' + eventNS, function() {
                            focusField();
                        })
                        .on('keydown' + eventNS, function(e) {
                            let handler = uiHandlers[e.keyCode];
                            // Special keys i.e. escape, tab
                            if (handler) { handler(e); }
                            // Normal keypress
                            else {
                                refreshMenu().then(showMenu);
                            }
                        })
                        .on('click' + eventNS, function() {
                            this.setSelectionRange(0, this.value.length);
                            focusField();
                        })
                        .on('blur' + eventNS, function() {
                            // IE fix (clicking scrollbar hides menu)
                            $timeout(() => {
                                if (uiState.denyBlur) { inputEl.focus(); }
                                else { blurField(); }
                            });
                        });

                    // DOM listeners on menu ul
                    ul.on('mousedown' + eventNS, () => {
                            uiState.denyBlur = true; // IE fix (above)
                            suppressFocus();
                        })
                        .on('mouseup' + eventNS, () => uiState.denyBlur = false)
                        .on('mouseleave' + eventNS, () => uiState.denyBlur = false)
                        .on('mouseleave' + eventNS, () => clearHighlight())
                        .on('mouseenter' + eventNS, '> li', e => {
                            const index = ul.find('.field-options-item').index(e.target);
                            if (index > -1) {
                                highlightOption(index);
                            } else {
                                clearHighlight();
                            }
                        });

                    $scope.$on('$destroy', deregister);
                }

                function showMenu() {
                    if (uiState.menuPopulated && uiState.fieldFocused) {
                        if (!uiState.menuShowing){
                            selectNone();
                            uiState.menuShowing = true;
                            $element.show();
                            if (ul.isOnScreen && !ul.isOnScreen()) {
                                scrollToMenu();
                            }
                        }
                    }
                    else {
                        hideMenu();
                    }
                }

                function hideMenu() {
                    if (!uiState.denyHide && uiState.menuShowing) {
                        uiState.menuShowing = false;
                        selectNone();
                        $element.hide();
                    }
                }

                function refreshMenu() {
                    return $q( res => debouncedPopulateMenu(res) );
                }

                function focusField() {
                    if (!uiState.denyFocus) {
                        uiState.fieldFocused = true;
                        refreshMenu().then(showMenu);
                    }
                }

                function blurField() {
                    uiState.fieldFocused = false;
                    hideMenu();
                }

                function scrollToMenu() {
                    if (!uiState.denyScroll) {
                        uiState.denyScroll = true;
                        let winPos = $(window).scrollTop(),
                            winHeight = $(window).height(),
                            ulPos = ul.offset().top,
                            ulHeight = ul.outerHeight(),
                            ulBottom = ulPos - winPos + ulHeight + 10, // Arbitrary gap
                            scrollPos = winPos + Math.min(ulBottom - winHeight, 150);

                        if (scrollPos > 0) {
                            $('html, body').animate({scrollTop: scrollPos}, 200);
                        }
                    }
                }

                function getSelectedOption() {
                    if (uiState.selectedIndex > -1){
                        return $element.find('li').eq(
                            uiState.selectableOptions[uiState.selectedIndex].domIndex
                        );
                    }
                }

                function selectCurrOption() {
                    if (uiState.menuShowing) {
                        let op = getSelectedOption();
                        if (op) {
                            op.trigger('click');
                        }
                        hideMenu();
                    }
                }

                function selectItem(params: any) {
                    let item = params.item;
                    if (params.e) {
                        params.e.preventDefault();
                        params.e.stopPropagation();
                    }
                    suppressFocus();
                    hideMenu();
                    if (item && !item.header) {
                        self.$scope.$emit('fieldOptionSelected', item);
                        $timeout(() => {
                            selectHandler(params); // `select` attribute
                        });
                    }
                }

                function populateMenu(data: any[]) {
                    clearMenu();

                    if (data) {
                        let opsCount = 0;
                        data.forEach((o, i) => {

                            // restricted Code for directive
                            if(
                                self.restricted != null
                                && o.Code !== self.restricted
                                && o.Title !== 'Airports')
                            {
                                return;
                            }

                            $(`<li class="${getItemClasses(o)}">
                                    ${getLabelHTML(o)}
                                </li>`)
                                .click(e => selectItem({item:o, e:e}))
                                .appendTo(ul);

                            // Index non-headers
                            if (!o.header) {
                                uiState.selectableOptions[opsCount++] = {domIndex: i, item: o};
                            }
                        });
                        uiState.selectableOpsCount = opsCount;

                        if (opsCount) {
                            selectNone();
                            uiState.menuPopulated = true;
                        }
                        else {
                            hideMenu();
                            clearMenu();
                        }
                    }
                }

                function clearMenu() {
                    uiState.selectableOptions = {};
                    uiState.selectableOpsCount = 0;
                    uiState.menuPopulated = false;
                    ul.empty();
                }

                function getLabelHTML(item: any) {
                    if (item.header) {
                        return formatHeader(item);
                    }
                    let label = formatLabel(item),
                        fieldVal = inputEl.val(),
                        term = fieldVal && fieldVal.trim().toLowerCase();
                    if (term) {
                        let termPos = label.toLowerCase().indexOf(term);
                        if (termPos > -1) {
                            let termEnd = termPos + term.length,
                                [str0, str1, str2] = [
                                    label.substring(0, termPos),
                                    label.substring(termPos, termEnd),
                                    label.substring(termEnd)
                                ];
                            label = `${str0}<span class="field-options-highlight">${str1}</span>${str2}`;
                        }
                    }
                    // let icon = '';
                    // if (item.icon) {
                    //     const cssClass = item.isChild ? 'field-options-child' : '';
                    //     icon = `<i class="icon-${item.icon} ${cssClass}"></i>`;
                    // }
                    //label = `${icon} ${label}`;
                    return label;
                }

                function highlightOption(i: number) {
                    clearHighlight();
                    uiState.selectedIndex = loopIndex(i, 0, uiState.selectableOpsCount-1);
                    const selectedOption = getSelectedOption();
                    if (selectedOption) {
                        selectedOption.addClass(selOptionClass);
                    }
                    return selectedOption;
                }

                function clearHighlight() {
                    $element.find('.' + selOptionClass).removeClass(selOptionClass);
                }

                function suppressFocus() {
                // Stops input focus showing menu again i.e. on menu click
                // NOTE: angular $timeout would run an unwanted digest
                    uiState.denyFocus = true;
                    setTimeout(() => uiState.denyFocus = false, 200);
                }

                function selectNone() {
                    uiState.selectedIndex = -1;
                    clearHighlight();
                }

                function traverseOptions(incr: number) {
                    if (uiState.menuShowing) {
                        let op:any = highlightOption(uiState.selectedIndex + incr),
                            opPos = op.position(),
                            opTop = opPos && opPos.top;

                        if (opTop) {
                            // Center option vertically
                            ul.scrollTop(
                                ul.scrollTop() + opTop - ul.height()/2 + op.outerHeight(true)/2
                            );
                        }
                    }
                }

                function loopIndex(i: number, start: number, end: number) {
                    return i < start ? end : (i > end ? start : i);
                }

                function deregister() {
                    inputEl.off(eventNS);
                    ul.off(eventNS);
                }

                function getItemClasses(item) {
                    let classes: string[] = [];
                    classes.push(item.header ? 'field-options-header' : 'field-options-item');
                    if (item.isGroup) {
                        classes.push('field-options-item-parent');
                    }
                    if (item.isChild) {
                        classes.push('field-options-item-child');
                    }
                    return classes.join(' ');
                }
            };
        }
    };

    angular
        .module('core')
        .directive('fieldOptions', (): ng.IDirective => {
            return {
                restrict: 'E',
                scope: {},
                controller: FieldOptions,
                controllerAs: 'fo',
                bindToController: {
                    inputField: '&', // JQuery element
                    data: '=', // Array of objects
                    select: '&', // Selection handler fn
                    headerFormat: '@', // Expression for header text format in menu (via $parse)
                    optionFormat: '@', // Expression for option text format in menu (via $parse)
                    noHide: '=', // Boolean to prevent menu hiding
                    classes: '@' // Additional CSS classes on wrapping <div>
                },
                replace: true,
                template: `<ul ng-class="'field-options ' + classes"></ul>`,
                link: (scope: any) => {
                    // Initialise once input field exists
                    let unwatchInit = scope.$watch(
                        () => scope.fo.inputField(),
                        (inputEl: JQuery) => {
                            if (inputEl) {
                                unwatchInit();
                                scope.fo.createMenu(inputEl);
                            }
                        }
                    );
                }
            };
        });
}
